import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {AbstractComponent, ConfirmationCanvasComponent, TimingCacheOffcanvasComponent} from "@components";
import {
  Competitor,
  CompetitorSaveControlPointResult,
  CompetitorSaveControlPointResultStatus,
  CompetitorStatus,
  EventItem,
  Lookups,
  MeasurementTimeItemHolder,
  Waypoint,
  WaypointType,
  RaceSettings,
} from "@model";
import {
  CompetitorService,
  LoaderService,
  MeasurementsService,
  MeasurementTimeItemHolderService,
  ToastService,
  WaypointService
} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {NgbOffcanvas} from "@ng-bootstrap/ng-bootstrap";
import {Router} from "@angular/router";
import {TIMING_SCREEN} from "../../../../app-routing.module";
import {v4 as uuidv4} from "uuid";
import {Subject, of} from "rxjs";
import { switchMap } from 'rxjs/operators';

type KeyboardMode = 'numeric' | 'alpha';
interface ParsedTypedValue {
  startNumber: string;
  extension: string;
}

@Component({
    selector: 'app-timer',
    templateUrl: './timer.component.html',
    styleUrls: ['./timer.component.scss'],
    standalone: false
})
export class TimerComponent extends AbstractComponent implements OnInit {

  static readonly TIMEOUT_30 = 30000;
  @Input() event?: EventItem;
  @Input() waypoint?: Waypoint;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() isRoundRace: boolean = false;

  currentTime = '';
  typedValue = '';

  savedTime: Date | undefined;
  savingStatus: CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus.OK;
  measurementTimeItemHolders: MeasurementTimeItemHolder[] = [];
  competitor: Competitor | undefined;
  hasConcurrentRace: boolean = false;
  raceSettingsMap: Map<number, RaceSettings> = new Map<number, RaceSettings>();
  keyboardMode: KeyboardMode = 'numeric';

  private intervals: Map<"clock" | "count" , NodeJS.Timeout> = new Map();

  keys = [
    {label: '1'}, {label: '2'}, {label: '3'},
    {label: '4'}, {label: '5'}, {label: '6'},
    {label: '7'}, {label: '8'}, {label: '9'},
    {label: '*', class: 'special', id: -1}, {label: '0'}, {label: '⌫', class: 'delete', id: -2}
  ];

  alphaKeys = [
    {label: 'A'}, {label: 'B'}, {label: 'C'}, {label: 'D'}, {label: 'E'},
    {label: 'F'}, {label: 'G'}, {label: 'H'}, {label: 'I'}, {label: 'J'},
    {label: 'K'}, {label: 'L'}, {label: 'M'}, {label: 'N'}, {label: 'O'},
    {label: 'P'}, {label: 'Q'}, {label: 'R'}, {label: 'S'}, {label: 'T'},
    {label: 'U'}, {label: 'V'}, {label: 'W'}, {label: 'X'}, {label: 'Y'}, {label: 'Z'},
    {label: '*', class: 'special', id: -1}, {label: '⌫', class: 'delete', id: -2}
  ];

  activeKeys = this.keys;

  @ViewChild('typedInput') typedInputRef!: ElementRef<HTMLInputElement>;

  savedCount: number = 0
  upcomingCount: number = 0;
  private typedValueSubject = new Subject<string | undefined>();


  constructor(private translocoService: TranslocoService, private waypointService: WaypointService, private loaderService: LoaderService,
              private offcanvasService: NgbOffcanvas, private toastService: ToastService,private router: Router, private competitorService: CompetitorService,
              private measurementsService: MeasurementsService, private measurementTimeItemHolderService: MeasurementTimeItemHolderService) {
    super();
  }


  ngOnInit() {
    this.setTime();
    this.getCount();
    this.initTimers();
    this.initHolder();
    this.subscribeToTypedValueChange();
    this.hasConcurrentRace = false;
    this.event?.racesList?.forEach(r => {
      if (r.id && r.raceSettings) {
        this.raceSettingsMap.set(r.id, r.raceSettings);
      }
      if (RaceSettings.isConcurrentRace(r.raceSettings)) {
        this.hasConcurrentRace = true;
      }
    });
  }

  subscribeToTypedValueChange() {
    this.subscriptions.push(this.typedValueSubject
        .pipe(
            switchMap((val: string | undefined) => {
              if (!val) return of(undefined);
              const parsed = this.parseTypedValue(val);
              if (!parsed) return of(undefined);

              const startNumber = parsed.startNumber.trim();
              const extension = parsed.extension.trim();

              if (startNumber.length < 1) {
                return of(undefined);
              }

              return this.measurementsService.ping().pipe(
                  switchMap(isOnline => {
                    if (!isOnline) {
                      return of(undefined);
                    }

                    return this.competitorService.fetchCompetitor(
                        startNumber,
                        undefined,
                        this.event!.id!,
                        this.waypoint?.id,
                        this.isRoundRace,
                        extension || undefined,
                    );
                  })
              );
            })
        )
        .subscribe(competitor => {
          this.competitor = competitor;
        }));
  }

  initTimers() {
    this.intervals.set("clock", setInterval(() => {
      this.setTime();
    }, 1000))
    this.intervals.set("count", setInterval(()=> {
      this.getCount();
    }, TimerComponent.TIMEOUT_30));
  }

  getCount() {
    this.measurementsService.ping().subscribe(isOnline => {
      if (isOnline) {
        this.waypointService.findMeasurementWaypointCount(this.event!.id!, this.waypoint!.id!).subscribe(val => {
          this.savedCount = val?.finished ?? 0;
          this.upcomingCount = val?.total ?? 0;
        })
      }
    })
  }

  setTime() {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString('hr-HR');
  }

  initHolder() {
    this.measurementTimeItemHolderService.currentHolder.subscribe(value => {
      this.measurementTimeItemHolders = value ?? [];
    })
    this.measurementTimeItemHolderService.initFromCache(!this.isRoundRace, this.event!.id!, this.waypoint?.id)
  }

  /** Digits, then optionally letters — e.g. "500", "500A" are valid;
   *  "A500", "5A0" are not. Used both to validate a keystroke before it's
   *  accepted and to split the finished value into startNumber/extension. */
  private isValidTypedValue(value: string): boolean {
    return /^\d*[A-Za-z]*$/.test(value);
  }

  parseTypedValue(value: string = this.typedValue): ParsedTypedValue | undefined {
    const match = value.match(/^(\d+)([A-Za-z]*)$/);
    if (!match) {
      return undefined;
    }
    return {
      startNumber: match[1],
      extension: match[2]
    };
  }

  onKeyPressed(key: string, id: number | undefined) {
    this.savingStatus = CompetitorSaveControlPointResultStatus.OK;
    if (id === -1) {
      this.typedValue = this.typedValue.slice(0, -1);
    }
    else if (id === -2) {
      this.openResetDialog();
      return;
    } else {
      const candidate = this.typedValue + key;
      if (!this.isValidTypedValue(candidate)) {
        return;
      }
      this.typedValue = candidate;
    }
    if (this.typedValue && this.typedValue.trim().length === 1) {
      this.savedTime = new Date();
    }

    this.typedValueSubject.next(this.typedValue);

    setTimeout(() => {
      const inputEl = this.typedInputRef?.nativeElement;
      if (inputEl) {
        inputEl.scrollLeft = inputEl.scrollWidth;
      }
    });
  }

  resetValues() {
    this.offcanvasService.dismiss();
    this.typedValue = "";
    this.typedValueSubject.next(undefined);
    this.savedTime = undefined;
    this.savingStatus = CompetitorSaveControlPointResultStatus.OK;
  }


  emptyCache() {
    this.measurementTimeItemHolderService.deleteHolderForEventCheckPoint(!this.isRoundRace, this.event!.id!, this.waypoint?.id)
    this.offcanvasService.dismiss();
  }


  openResetDialog() {
    const offcanvasRef = this.offcanvasService.open(ConfirmationCanvasComponent, {
            position: 'bottom',
            scroll: false,
            panelClass: 'mobile-filter-sheet confirmation-canvas',
            backdropClass: 'offcanvas-backdrop'
    });

    offcanvasRef.componentInstance.title = this.translocoService.translate("timing.clear.input.confirmation.title");
    offcanvasRef.componentInstance.text = this.translocoService.translate("timing.clear.input.confirmation.text");

    offcanvasRef.componentInstance.onConfirm.subscribe(() => {
      this.resetValues()
    });
  }


  onSaveTime(competitorStatus?: CompetitorStatus.TLE | CompetitorStatus.DSQ | CompetitorStatus.DNF) {
    if ((this.typedValue && this.typedValue.trim().length > 0) && this.savedTime) {
      const parsed = this.parseTypedValue();
      if (!parsed) {
        return;
      }
      const {startNumber, extension} = parsed;

      const competitor = this.competitor ?? new Competitor();
      let status: CompetitorStatus | undefined;
      if (competitorStatus) {
        status = competitorStatus;
      }
      else if (this.waypoint?.waypointTypeId === WaypointType.FINISH_WAYPOINT) {
        status = CompetitorStatus.FIN;
      }

      competitor.controlPointsTimes = [{
        time: this.savedTime,
        controlWaypoint: this.waypoint,
        competitorStatus: status
      }];
      competitor.startNumber = startNumber;
      competitor.startNumberExtension = extension || undefined;
      competitor.key = uuidv4();

      this.addCompetitorToCache(competitor, CompetitorSaveControlPointResultStatus.OK);
      this.resetValues();
      this.measurementsService.ping().subscribe(isOnline => {
        this.updateInCache(competitor, CompetitorSaveControlPointResultStatus.OK, !!isOnline);
        if (isOnline) {
          const handleSaveSuccess = (results: CompetitorSaveControlPointResult[]) => {
            const result: CompetitorSaveControlPointResult | undefined = results.at(0);
            const saveStatus = result?.status ?? CompetitorSaveControlPointResultStatus.OK;
            this.savingStatus = saveStatus;
            if (saveStatus === CompetitorSaveControlPointResultStatus.OK) {
              this.removeFromCache(competitor);
            }
            else {
              this.updateInCache(competitor, saveStatus, false)
            }
            this.getCount();
          }
          const handleSaveError = () => {
            this.getCount();
          }
          if (!this.isRoundRace) {
            this.measurementsService.saveStartNumberControlPoints(
                [competitor], this.event!.id!, handleSaveSuccess, handleSaveError)
          } else {
            //todo
          }
        }});
    }
  }

  setKeyboardMode(mode: KeyboardMode): void {
    this.keyboardMode = mode;
    this.activeKeys = (mode === 'alpha') ? this.alphaKeys : this.keys;
  }

  openCache() {
    if (this.measurementTimeItemHolders.length ?? 0 > 0) {
      const offcanvasRef = this.offcanvasService.open(TimingCacheOffcanvasComponent, {
        position: 'bottom',
        scroll: false,
        panelClass: 'mobile-filter-sheet timer-cache-canvas',
        backdropClass: 'offcanvas-backdrop'
      });

      offcanvasRef.componentInstance.event = this.event;
      offcanvasRef.componentInstance.isRoundRace = this.isRoundRace;
      offcanvasRef.componentInstance.waypoint = this.waypoint;
      offcanvasRef.componentInstance.raceSettingsMap = this.raceSettingsMap;

      offcanvasRef.componentInstance.delete.subscribe(() => {
        this.emptyCache();
        offcanvasRef.dismiss();
      });
    }
  }

  addCompetitorToCache(competitor: Competitor, saveResult: CompetitorSaveControlPointResultStatus) {
    this.measurementTimeItemHolders.push({
      competitor: competitor,
      saveResult: saveResult,
      startNumber: competitor.startNumber,
      time: competitor.controlPointsTimes?.at(0)?.time,
      startNumberExtension: competitor.startNumberExtension
    });
    this.saveCache();

    this.measurementsService.ping().pipe(
        switchMap(isOnline => {
          const shouldFetch = isOnline && !competitor.id && competitor.startNumber;
          return shouldFetch
              ? this.competitorService.fetchCompetitor(
                  competitor.startNumber!,
                  undefined,
                  this.event!.id!,
                  this.waypoint?.id,
                  this.isRoundRace,
                  competitor.startNumberExtension,
              )
              : of(competitor);
        })
    ).subscribe(fetchedCompetitor => {
      const index = this.measurementTimeItemHolders.findIndex(
          item => item.competitor?.key === competitor.key
      );

      if (index !== -1) {
        const saved = this.measurementTimeItemHolders[index].competitor;
        const mergedCompetitor = { ...fetchedCompetitor, ...saved };
        this.measurementTimeItemHolders[index] = {
          ...this.measurementTimeItemHolders[index],
          competitor: mergedCompetitor
        };
        this.saveCache();
      }
    });
  }

  removeFromCache(competitor: Competitor) {
    this.measurementTimeItemHolders = this.measurementTimeItemHolders.filter(item => item.competitor?.key !== competitor.key);
    this.saveCache();
  }

  updateInCache(competitor: Competitor, saveResult: CompetitorSaveControlPointResultStatus, inProgress: boolean) {
    const index = this.measurementTimeItemHolders.findIndex(item => item.competitor?.key === competitor.key);
    if (index !== -1) {
      this.measurementTimeItemHolders[index] = {...this.measurementTimeItemHolders[index], saveResult: saveResult, inProgress: inProgress};
    }
    this.saveCache();
  }

  saveCache() {
    this.measurementTimeItemHolderService.saveHolderForEventCheckPoint(!this.isRoundRace, this.event!.id!, this.waypoint?.id, this.measurementTimeItemHolders)
  }

  back(): void {
    this.router.navigate([TIMING_SCREEN], {queryParams: {eventId: this.event!.id!}});
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.typedValueSubject.complete();
    this.measurementTimeItemHolderService.empty();
    this.intervals.forEach((intervalId, key) => {
      clearInterval(intervalId);
    });
  }

  protected readonly CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus;
  protected readonly CompetitorStatus = CompetitorStatus;

}